/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import { type OIDCProvider } from './service';
import { SsoButton } from './atoms';

interface Props {
  providers: OIDCProvider[];
  dividerLabel: string;
  onSelect: (p: OIDCProvider) => void;
}

const Divider: FC<{ label: string }> = ({ label }) => (
  <div
    style={{
      margin: '24px 0 18px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      color: 'var(--text-4)',
      fontSize: 12,
    }}
  >
    <span style={{ flex: 1, height: 1, background: 'var(--border-1)' }} />
    {label}
    <span style={{ flex: 1, height: 1, background: 'var(--border-1)' }} />
  </div>
);

export const OidcSection: FC<Props> = ({
  providers,
  dividerLabel,
  onSelect,
}) => {
  if (providers.length === 0) {
    return null;
  }
  const consumer = providers.filter(p =>
    ['google', 'github'].includes(p.name.toLowerCase()),
  );
  const enterprise = providers.filter(
    p => !['google', 'github'].includes(p.name.toLowerCase()),
  );

  return (
    <>
      <Divider label={dividerLabel} />
      <div
        data-testid="login.section.oidc"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
        }}
      >
        {consumer.map(p => (
          <SsoButton key={p.id} provider={p} onClick={() => onSelect(p)} />
        ))}
        {enterprise.map(p => (
          <SsoButton key={p.id} provider={p} full onClick={() => onSelect(p)} />
        ))}
      </div>
    </>
  );
};
